import {
  Box,
  ContentField,
  ExtractorOacJsonResponse,
  GroupField,
  Property,
  Vertex,
} from "../types/extractorOacResponse";
import { LAOacJsonResponse } from "../types/laOacResponse";
import {
  OCROacJsonResponse,
  convertResponseToImageOCROacJson,
} from "../types/ocrOacResponse";

// 원본 객체를 보호하기 위한 깊은 복사 유틸리티 함수
const deepCopy = (result: any): any => {
  return JSON.parse(JSON.stringify(result));
};

export const processExtractorResult = async (
  result: ExtractorOacJsonResponse,
  resize: boolean,
): Promise<ExtractorOacJsonResponse> => {
  const images: string[] = result.imageBase64;
  const ratio: number[] = Array(images.length).fill(1);
  const orgHeight: number[] = result.metadata.pages.map(page => page.height);
  const forceResize = true;
  const isResize = forceResize || resize;

  const resultCopy = deepCopy(result);
  fillEmptyExtractorFields(resultCopy);

  if (isResize) {
    // Promise.all()을 사용하여 모든 이미지 로딩 완료 여부 추적
    // Promise 배열 생성
    const imageLoadPromises: Promise<void>[] = images.map((image, idx) => {
      return new Promise<void>((resolve, reject) => {
        const newImageSource = "data:image/jpeg;base64," + image;
        const img: HTMLImageElement = new Image();

        img.onload = function () {
          const baseHeight = img.height;
          if (
            resultCopy.metadata &&
            resultCopy.metadata.pages.length > 0 &&
            resultCopy.metadata.pages[idx].height > 0
          ) {
            ratio[idx] = baseHeight / orgHeight[idx];
          }
          resolve(); // Promise를 해결하여 이미지 로딩이 완료되었음을 알림
        };

        img.onerror = function () {
          reject(new Error("Failed to load image")); // 이미지 로딩 실패 시 reject 호출
        };

        img.src = newImageSource;
      });
    });
    await Promise.all(imageLoadPromises)
      .then(() => {
        // 모든 이미지 로딩이 완료된 후 실행될 코드
        for (let i = 0; i < resultCopy.fields.length; i++) {
          const field = resultCopy.fields[i];
          if (field.type === "group") {
            const f = field as GroupField;
            f.properties = resizeGroupBoxes(f.properties, ratio);
          } else {
            const f = field as ContentField;
            f.boundingBoxes = f.boundingBoxes
              ? resizeBoxes(f.boundingBoxes, ratio)
              : [];
          }
        }
      })
      .catch(error => {
        console.error("Error loading images:", error);
        throw error;
      });
  }

  return resultCopy;
};

export const processOCRResult = async (
  result: OCROacJsonResponse,
  resize: boolean,
): Promise<OCROacJsonResponse> => {
  const images: string[] = result.imageBase64;
  const ratio: number[] = Array(images.length).fill(1);
  const orgHeight: number[] = result.metadata.pages.map(page => page.height);

  const resultCopy = deepCopy(result);
  fillEmptyOCRFields(resultCopy);

  if (resize) {
    // Promise.all()을 사용하여 모든 이미지 로딩 완료 여부 추적
    // Promise 배열 생성
    const imageLoadPromises: Promise<void>[] = images.map((image, idx) => {
      return new Promise<void>((resolve, reject) => {
        const newImageSource = "data:image/jpeg;base64," + image;
        const img: HTMLImageElement = new Image();

        img.onload = function () {
          const baseHeight = img.height;
          if (resultCopy.metadata) {
            ratio[idx] = baseHeight / orgHeight[idx];
          }
          resolve(); // Promise를 해결하여 이미지 로딩이 완료되었음을 알림
        };

        img.onerror = function () {
          reject(new Error("Failed to load image")); // 이미지 로딩 실패 시 reject 호출
        };

        img.src = newImageSource;
      });
    });
    await Promise.all(imageLoadPromises)
      .then(() => {
        const pages = convertResponseToImageOCROacJson(resultCopy).pages;
        resultCopy.pages = pages;
      })
      .then(() => {
        // 모든 이미지 로딩이 완료된 후 실행될 코드
        for (let i = 0; i < resultCopy.pages.length; i++) {
          const page = resultCopy.pages[i];
          page.lines.forEach((line: any) => {
            line.boundingBox.forEach((box: any) => {
              box.vertices = scaleBoxes(box.vertices, ratio[i]);
            });
          });
        }
      })
      .catch(error => {
        console.error("Error loading images:", error);
        throw error;
      });
  }

  return resultCopy;
};

/**
 * 재구성 요소(`elements`)의 빈 `sub_category` 를 raw 요소(`elements_dp`)에서 채운다.
 *
 * 미리보기 라벨(`1 - chart / bar chart`)은 재구성 요소의 `sub_category` 를 읽는데,
 * 이 필드를 채우는 것은 플레이그라운드 백엔드(`/api/layout-analysis` 의
 * `convert_respose`)다. 그보다 오래된 백엔드가 떠 있으면 필드가 아예 없어 라벨이
 * 카테고리만 보여준다. 같은 응답의 `elements_dp` 에는 API 원본이 그대로 들어 있으니
 * 그것을 폴백으로 쓴다. 재구성 요소가 이미 값을 갖고 있으면 건드리지 않는다.
 */
export const backfillSubCategory = (
  result: LAOacJsonResponse,
): LAOacJsonResponse => {
  const raw = result.elements_dp;
  if (!result.elements?.length || !raw?.length) {
    return result;
  }

  const subCategoryById = new Map(
    raw
      .filter(element => element.sub_category)
      .map(element => [element.id, element.sub_category as string]),
  );
  if (subCategoryById.size === 0) {
    return result;
  }

  result.elements.forEach(element => {
    if (!element.sub_category) {
      const subCategory = subCategoryById.get(element.id);
      if (subCategory) {
        element.sub_category = subCategory;
      }
    }
  });

  return result;
};

export const processLAResult = async (
  result: LAOacJsonResponse,
  resize: boolean,
): Promise<LAOacJsonResponse> => {
  const images: string[] = result.imageBase64;
  const ratio: number[] = Array(images.length).fill(1);
  const orgHeight: number[] = result.metadata.pages.map(page => page.height);

  const resultCopy = backfillSubCategory(deepCopy(result));

  if (resize) {
    // Promise.all()을 사용하여 모든 이미지 로딩 완료 여부 추적
    // Promise 배열 생성
    const imageLoadPromises: Promise<void>[] = images.map((image, idx) => {
      return new Promise<void>((resolve, reject) => {
        const newImageSource = "data:image/jpeg;base64," + image;
        const img: HTMLImageElement = new Image();

        img.onload = function () {
          const baseHeight = img.height;
          if (resultCopy.metadata) {
            ratio[idx] = baseHeight / orgHeight[idx];
          }
          resolve(); // Promise를 해결하여 이미지 로딩이 완료되었음을 알림
        };

        img.onerror = function () {
          reject(new Error("Failed to load image")); // 이미지 로딩 실패 시 reject 호출
        };

        img.src = newImageSource;
      });
    });
    await Promise.all(imageLoadPromises).catch(error => {
      console.error("Error loading images:", error);
      throw error;
    });
  }

  return resultCopy;
};

const fillEmptyExtractorFields = (result: ExtractorOacJsonResponse) => {
  for (let i = 0; i < result.fields.length; i++) {
    const field = result.fields[i];
    if (field.type === "group") {
      const f = field as GroupField;
      for (let j = 0; j < f.properties.length; j++) {
        if (!f.properties[j].boundingBoxes) f.properties[j].boundingBoxes = [];
      }
    } else {
      const f = field as ContentField;
      if (!f.boundingBoxes) f.boundingBoxes = [];
    }
  }
};

const fillEmptyOCRFields = (result: OCROacJsonResponse) => {
  for (let i = 0; i < result.pages.length; i++) {
    const page = result.pages[i];
    if (!page.words) page.words = [];
  }
};

const resizeGroupBoxes = (
  properties: Property[],
  ratios: number[],
): Property[] => {
  const retProperties: Property[] = [];
  properties.forEach(p => {
    retProperties.push({
      value: p.value,
      id: p.id,
      type: p.type,
      key: p.key,
      confidence: p.confidence,
      refinedValue: p.refinedValue,
      boundingBoxes: p.boundingBoxes
        ? resizeBoxes(p.boundingBoxes, ratios)
        : [],
    });
  });
  return retProperties;
};

const scaleBoxes = (vertices: Vertex[], ratio: number): Vertex[] => {
  return vertices.map(vertex => ({
    x: vertex.x * ratio,
    y: vertex.y * ratio,
  }));
};

const _getArea = (box: Box) => {
  const [minX, minY, maxX, maxY] = [
    box.vertices[0].x,
    box.vertices[0].y,
    box.vertices[2].x,
    box.vertices[2].y,
  ];
  return (maxX - minX) * (maxY - minY);
};

const resizeBoxes = (boxes: Box[], ratios: number[]): Box[] => {
  if (!boxes || boxes.length == 0) return [];
  const retBoxes: Box[] = [];
  boxes.forEach(box => {
    retBoxes.push({
      page: box.page,
      vertices: scaleBoxes(box.vertices, ratios[box.page - 1] ?? 1),
    });
  });
  return retBoxes;
};
